import { useState, useEffect, useRef } from 'react';
import { Lock, Shield, Eye, EyeOff, AlertCircle, Clock, X } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { AuthError } from '@/hooks/useAdminAuth';

interface AdminLoginProps {
  onLogin: (email: string, password: string, mfaCode?: string) => Promise<void>;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showMFA, setShowMFA] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [rateLimitInfo, setRateLimitInfo] = useState<{
    isRateLimited: boolean;
    retryAfter: number;
    initialRetryAfter: number;
    message: string;
  } | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Countdown timer for rate limiting
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (rateLimitInfo?.isRateLimited && rateLimitInfo.retryAfter > 0) {
      intervalRef.current = setInterval(() => {
        setRateLimitInfo((prev) => {
          if (!prev || prev.retryAfter <= 1) return null;
          return { ...prev, retryAfter: prev.retryAfter - 1 };
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [rateLimitInfo?.isRateLimited, rateLimitInfo?.initialRetryAfter]);

  const formatRetryTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
    return `${remainingSeconds}s`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLoginError(null);

    try {
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      await onLogin(email, password, mfaCode || undefined);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';

      // Check if this is an AuthError with rate-limit info
      if (err instanceof AuthError && err.retryAfter) {
        const retryAfter = err.retryAfter;
        setRateLimitInfo({
          isRateLimited: true,
          retryAfter,
          initialRetryAfter: retryAfter,
          message:
            'Too many login attempts detected. This is a security measure to protect admin accounts from brute force attacks.',
        });
        setLoginError(null);
      } else if (
        errorMessage.includes('Too many authentication attempts') ||
        errorMessage.includes('retry_after')
      ) {
        setRateLimitInfo({
          isRateLimited: true,
          retryAfter: 900,
          initialRetryAfter: 900,
          message:
            'Too many login attempts detected. This is a security measure to protect admin accounts from brute force attacks.',
        });
        setLoginError(null);
      } else {
        setLoginError(errorMessage);
        setRateLimitInfo(null);

        if (errorMessage.includes('MFA') || errorMessage.includes('2FA')) {
          setShowMFA(true);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDarkMode = (() => {
    try {
      const saved = localStorage.getItem('admin_theme');
      return saved ? saved === 'dark' : true;
    } catch {
      return true;
    }
  })();

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${
        isDarkMode
          ? 'bg-gradient-to-br from-slate-900 to-slate-800'
          : 'bg-gradient-to-br from-blue-50 to-indigo-100'
      }`}
    >
      <div className="max-w-md w-full space-y-8 p-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-cyan-600' : 'bg-indigo-600'}`}>
              <Shield className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            TraderTekno Admin
          </h2>
          <p className={`mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Sign in to access the admin dashboard
          </p>
        </div>

        {/* Login Form */}
        <form id="admin-login-form" className="mt-8 space-y-6" onSubmit={(e) => void handleSubmit(e)}>
          <div className="space-y-4">
            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className={`block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}
              >
                Admin Email
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`appearance-none relative block w-full px-3 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:z-10 sm:text-sm ${
                    isDarkMode
                      ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500/50 focus:border-cyan-500'
                      : 'border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  placeholder="Email admin"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                </div>
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label
                htmlFor="password"
                className={`block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}
              >
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`appearance-none relative block w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:z-10 sm:text-sm ${
                    isDarkMode
                      ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500/50 focus:border-cyan-500'
                      : 'border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className={`h-5 w-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  ) : (
                    <Eye className={`h-5 w-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                  )}
                </button>
              </div>
            </div>

            {/* MFA Input (conditional) */}
            {showMFA && (
              <div>
                <label
                  htmlFor="mfa"
                  className={`block text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}
                >
                  MFA Code
                </label>
                <div className="mt-1">
                  <input
                    id="mfa"
                    name="mfa"
                    type="text"
                    maxLength={6}
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                    className={`appearance-none relative block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:z-10 sm:text-sm text-center tracking-widest ${
                      isDarkMode
                        ? 'bg-slate-700/50 border-slate-600 text-white placeholder-slate-400 focus:ring-cyan-500/50 focus:border-cyan-500'
                        : 'border-gray-300 placeholder-gray-500 text-gray-900 focus:ring-indigo-500 focus:border-indigo-500'
                    }`}
                    placeholder="123456"
                  />
                  <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Rate Limit Notification */}
          {rateLimitInfo?.isRateLimited && (
            <div
              className={`border rounded-lg p-4 ${
                isDarkMode ? 'bg-amber-900/20 border-amber-700/40' : 'bg-amber-50 border-amber-200'
              }`}
            >
              <div className="flex">
                <div className="flex-shrink-0">
                  <Clock className={`h-5 w-5 ${isDarkMode ? 'text-amber-400' : 'text-amber-400'}`} />
                </div>
                <div className="ml-3 flex-1">
                  <h3 className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                    Account Temporarily Locked
                  </h3>
                  <div className={`mt-2 text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                    <p className="mb-2">{rateLimitInfo.message}</p>
                    <div
                      className={`border rounded px-3 py-2 ${
                        isDarkMode ? 'bg-amber-900/30 border-amber-700/50' : 'bg-amber-100 border-amber-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}
                        >
                          Time until retry available:
                        </span>
                        <span
                          className={`text-sm font-bold tabular-nums ${isDarkMode ? 'text-amber-200' : 'text-amber-900'}`}
                        >
                          {formatRetryTime(rateLimitInfo.retryAfter)}
                        </span>
                      </div>
                      <div
                        className={`mt-1 rounded-full h-1 ${isDarkMode ? 'bg-amber-800/50' : 'bg-amber-200'}`}
                      >
                        <div
                          className={`h-1 rounded-full transition-all duration-1000 ease-linear ${
                            isDarkMode ? 'bg-amber-400' : 'bg-amber-600'
                          }`}
                          style={{
                            width: `${Math.max(0, (rateLimitInfo.retryAfter / rateLimitInfo.initialRetryAfter) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                    <p className={`mt-3 text-xs ${isDarkMode ? 'text-amber-500' : 'text-amber-600'}`}>
                      <strong>Why am I seeing this?</strong> Multiple failed login attempts were detected from
                      your IP address. This security measure protects admin accounts from unauthorized access
                      attempts.
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0 ml-4">
                  <button
                    type="button"
                    onClick={() => setRateLimitInfo(null)}
                    className={`transition-colors ${
                      isDarkMode ? 'text-amber-500 hover:text-amber-300' : 'text-amber-400 hover:text-amber-600'
                    }`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {loginError && !rateLimitInfo?.isRateLimited && (
            <div
              className={`border rounded-lg p-4 ${
                isDarkMode ? 'bg-red-900/20 border-red-800/40' : 'bg-red-50 border-red-200'
              }`}
            >
              <div className="flex">
                <AlertCircle className={`h-5 w-5 ${isDarkMode ? 'text-red-400' : 'text-red-400'}`} />
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>
                    Authentication Error
                  </h3>
                  <div className={`mt-2 text-sm ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                    {loginError}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isSubmitting || rateLimitInfo?.isRateLimited}
              className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                isDarkMode
                  ? 'bg-cyan-600 hover:bg-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900'
                  : 'bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500 focus:ring-offset-white'
              }`}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Signing in...
                </>
              ) : rateLimitInfo?.isRateLimited ? (
                <>
                  <Clock className="mr-2 h-4 w-4" />
                  Retry in {formatRetryTime(rateLimitInfo.retryAfter)}
                </>
              ) : (
                'Sign in to Admin Dashboard'
              )}
            </button>
          </div>
        </form>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
            This is a secure admin area. All actions are logged and monitored.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
